import React from 'react';
import { NodeProperty } from '@shared/types/node-properties';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MultiOptionsPropertyRendererProps {
  property: NodeProperty;
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
  disabled?: boolean;
  error?: string;
  hideLabel?: boolean;
}

export const MultiOptionsPropertyRenderer: React.FC<MultiOptionsPropertyRendererProps> = ({
  property,
  value = [],
  onChange,
  disabled = false,
  error,
  hideLabel = false,
}) => {
  const options = Array.isArray(property.options) ? property.options : [];

  const toggleOption = (optionValue: string | number) => {
    const currentValues = Array.isArray(value) ? value : [];
    const index = currentValues.findIndex((v) => String(v) === String(optionValue));
    
    if (index >= 0) {
      onChange(currentValues.filter((_, i) => i !== index));
    } else {
      onChange([...currentValues, optionValue]);
    }
  };

  const removeOption = (optionValue: string | number) => {
    const currentValues = Array.isArray(value) ? value : [];
    onChange(currentValues.filter((v) => String(v) !== String(optionValue)));
  };

  const isChecked = (optionValue: string | number) => {
    const currentValues = Array.isArray(value) ? value : [];
    return currentValues.some((v) => String(v) === String(optionValue));
  };

  // If hideLabel is true, just render the control without label/description
  if (hideLabel) {
    return (
      <div className="space-y-3">
        {/* Selected values */}
        {value.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {value.map((val) => {
              const option = options.find((opt) => String(opt.value) === String(val));
              return (
                <Badge key={String(val)} variant="secondary" className="pr-1">
                  {option?.name || val}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                    onClick={() => removeOption(val)}
                    disabled={disabled}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              );
            })}
          </div>
        )}

        {/* Options list */}
        <div className="space-y-2 border rounded-md p-3 max-h-64 overflow-y-auto">
          {options.map((option: any) => (
            <div key={String(option.value)} className="flex items-start space-x-2">
              <Checkbox
                id={`${property.key}-${option.value}`}
                checked={isChecked(option.value ?? '')}
                onCheckedChange={() => toggleOption(option.value ?? '')}
                disabled={disabled}
              />
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor={`${property.key}-${option.value}`}
                  className="font-normal cursor-pointer"
                >
                  {option.name}
                </Label>
                {option.description && (
                  <span className="text-xs text-muted-foreground">{option.description}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <Label>
          {property.label}
          {property.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {property.description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{property.description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Selected values */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((val) => {
            const option = options.find((opt: any) => String(opt.value) === String(val));
            return (
              <Badge key={String(val)} variant="secondary" className="pr-1">
                {option?.name || val}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                  onClick={() => removeOption(val)}
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Options list */}
      <div className="space-y-2 border rounded-md p-3 max-h-64 overflow-y-auto">
        {options.map((option: any) => (
          <div key={String(option.value)} className="flex items-start space-x-2">
            <Checkbox
              id={`${property.key}-${option.value}`}
              checked={isChecked(option.value ?? '')}
              onCheckedChange={() => toggleOption(option.value ?? '')}
              disabled={disabled}
            />
            <div className="flex flex-col flex-1">
              <Label
                htmlFor={`${property.key}-${option.value}`}
                className="font-normal cursor-pointer"
              >
                {option.name}
              </Label>
              {option.description && (
                <span className="text-xs text-muted-foreground">{option.description}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {property.hint && !error && (
        <p className="text-xs text-muted-foreground">{property.hint}</p>
      )}
    </div>
  );
};
